<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref } from 'vue'
import Lenis from '@studio-freight/lenis'
import Header from '../components/HeaderView.vue'
import Footer from '../components/FooterView.vue'
import Breadcrumbs from '../components/Breadcrumbs.vue'
import { blogApi, supabase } from '../lib/supabase'
import { useSEO } from '../composables/useSEO'
import type { Post } from '../lib/supabase'

let lenis: Lenis | null = null

// --- INICIO DE CAMBIOS ---

// 1. Crear estado reactivo para los posts
// CAMBIO: La estructura de `post_tags` ahora es más simple
const allPosts = ref<
  (Post & {
    author_name: string | null
    post_tags: { name: string }[]
    content_sections?: Array<{
      type: string
      content: { text?: string }
    }>
  })[]
>([])
const featuredPost = ref<(typeof allPosts.value)[0] | null>(null)
const regularPosts = ref<typeof allPosts.value>([])
const isLoading = ref(true)
const errorMsg = ref<string | null>(null)
const { updateMetaTags } = useSEO()

// Función para obtener excerpt del post
function getPostExcerpt(post: (typeof allPosts.value)[0]): string {
  if (post.content_sections && post.content_sections.length > 0) {
    const firstParagraph = post.content_sections.find(
      (section) => section.type === 'PARAGRAPH' && section.content?.text
    )
    if (firstParagraph?.content?.text) {
      const text = firstParagraph.content.text
      return text.length > 150 ? text.substring(0, 150) + '...' : text
    }
  }
  // Fallback: usar título truncado
  return post.title.length > 150 ? post.title.substring(0, 150) + '...' : post.title
}

// Función para calcular tiempo de lectura
function getReadingTime(post: (typeof allPosts.value)[0]): number {
  if (post.content_sections && post.content_sections.length > 0) {
    const totalWords = post.content_sections
      .filter((section) => section.type === 'PARAGRAPH')
      .reduce((acc, section) => {
        const text = section.content?.text || ''
        return acc + text.split(/\s+/).length
      }, 0)
    return Math.max(1, Math.ceil(totalWords / 200))
  }
  return 5 // Default
}

// 2. Función para cargar los datos
async function fetchPosts() {
  try {
    isLoading.value = true
    // Usamos la función de la API que ya creaste
    const data = await blogApi.getPublishedPosts()

    console.log('📊 Posts obtenidos:', data) // Debug log

    // El tipo inferido de `data` es complejo, lo casteamos para mejor autocompletado
    // Necesitamos obtener content_sections para cada post para calcular excerpt y tiempo de lectura
    const typedData = (await Promise.all(
      (data as any[]).map(async (post) => {
        try {
          const { data: sections } = await supabase
            .from('content_sections')
            .select('type, content')
            .eq('post_id', post.id)
            .order('order')
            .limit(3) // Solo necesitamos los primeros para el excerpt

          return {
            ...post,
            content_sections: sections || [],
          }
        } catch {
          return { ...post, content_sections: [] }
        }
      })
    )) as typeof allPosts.value

    allPosts.value = typedData

    console.log('📊 Total de posts:', typedData.length) // Debug log
    console.log('📊 Posts destacado:', typedData[0]?.title) // Debug log
    console.log(
      '📊 Posts regulares:',
      typedData.slice(1).map((p) => p.title)
    ) // Debug log

    // Lógica para separar destacados de regulares
    // Tomamos el primer post como destacado (el más reciente)
    featuredPost.value = typedData.length > 0 ? (typedData[0] as (typeof allPosts.value)[0]) : null

    // Los posts regulares son todos excepto el destacado
    regularPosts.value = typedData.length > 1 ? typedData.slice(1) : []
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Error desconocido'
    errorMsg.value = `Error al cargar los posts: ${errorMessage}`
    console.error(error)
  } finally {
    isLoading.value = false
  }
}

// 3. Cargar los datos cuando el componente se monta
onMounted(async () => {
  await fetchPosts()

  // Actualizar SEO para la página del blog
  updateMetaTags({
    title: 'Blog - Artículos sobre CMMS, Mantenimiento y Gestión de Activos | Fourin',
    description:
      'Descubre artículos sobre gestión de activos, mantenimiento predictivo, CMMS/GMAO y eficiencia operativa. Guías, comparativas y casos de éxito para empresas en Colombia y LATAM.',
    keywords:
      'blog mantenimiento, artículos CMMS, gestión de activos, mantenimiento predictivo, blog industrial, GMAO Latinoamérica',
    url: 'https://fourin.co/blog',
    canonical: 'https://fourin.co/blog',
    type: 'website',
    organization: true,
    breadcrumbs: [
      { name: 'Inicio', url: '/' },
      { name: 'Blog', url: '/blog' },
    ],
  })

  // Inicializar Lenis
  lenis = new Lenis({
    lerp: 0.1,
    smoothWheel: true,
  })

  function raf(time: number) {
    lenis?.raf(time)
    requestAnimationFrame(raf)
  }

  requestAnimationFrame(raf)

  // Restablecer scroll al inicio de la página
  window.scrollTo({ top: 0, behavior: 'smooth' })
})

// --- FIN DE CAMBIOS ---

onBeforeUnmount(() => {
  if (lenis) {
    lenis.destroy()
  }
})
</script>

<template>
  <Header />
  <main>

    <!-- Hero Section -->
    <section class="blog-hero">
      <div class="container">
        <!--    <Breadcrumbs
          :items="[
            { name: 'Inicio', url: '/' },
            { name: 'Blog', url: '/blog' }
          ]"
        /> -->

        <div class="hero-content">
          <h1>Blog de Fourin</h1>
          <p class="hero-description">
            Explora las últimas tendencias en tecnología industrial, innovación y transformación
            digital. Nuestro equipo comparte insights, casos de estudio y perspectivas sobre el
            futuro de la industria.
          </p>
        </div>
      </div>
    </section>

    <!-- Skeleton Loader -->
    <div v-if="isLoading" class="skeleton-container">
      <div class="container">
        <!-- Featured Post Skeleton -->
        <section class="featured-post-skeleton">
          <div class="featured-skeleton-content">
            <div class="featured-skeleton-image"></div>
            <div class="featured-skeleton-details">
              <div class="skeleton-author">
                <div class="skeleton-avatar"></div>
                <div class="skeleton-author-text"></div>
              </div>
              <div class="skeleton-date"></div>
              <div class="skeleton-title"></div>
              <div class="skeleton-tags">
                <div class="skeleton-tag"></div>
                <div class="skeleton-tag"></div>
                <div class="skeleton-tag"></div>
              </div>
            </div>
          </div>
        </section>

        <!-- Posts Grid Skeleton -->
        <section class="posts-skeleton">
          <div class="section-header-skeleton">
            <div class="skeleton-section-title"></div>
            <div class="skeleton-button"></div>
          </div>

          <div class="posts-grid-skeleton">
            <div v-for="n in 6" :key="n" class="post-card-skeleton">
              <div class="skeleton-post-image"></div>
              <div class="skeleton-post-content">
                <div class="skeleton-post-category"></div>
                <div class="skeleton-post-title"></div>
                <div class="skeleton-post-excerpt"></div>
                <div class="skeleton-post-meta">
                  <div class="skeleton-post-author"></div>
                  <div class="skeleton-post-date"></div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="errorMsg" class="container">
      <div style="text-align: center; padding: 2rem; color: red">
        <p>{{ errorMsg }}</p>
      </div>
    </div>

    <!-- Featured Post -->
    <section class="featured-post" v-if="featuredPost && !isLoading">
      <div class="container">
        <div class="featured-content">
          <router-link :to="`/blog/${featuredPost.slug}`" class="featured-image-link">
            <div class="featured-image">
              <img
                :src="featuredPost.cover_image_url || '/placeholder-image.jpg'"
                :alt="featuredPost.title"
                loading="eager"
                fetchpriority="high"
                decoding="async"
              />
              <div class="featured-overlay">
                <h2>{{ featuredPost.title }}</h2>
              </div>
            </div>
          </router-link>

          <div class="featured-details">
            <div class="featured-badge">
              <span>Artículo Destacado</span>
            </div>

            <p class="featured-excerpt">{{ getPostExcerpt(featuredPost) }}</p>

            <div class="post-meta">
              <div class="author-info">
                <div class="author-avatar">
                  <span>{{ (featuredPost.author_name || 'Autor').charAt(0) }}</span>
                </div>
                <div class="author-details">
                  <span class="author-name">{{
                    featuredPost.author_name || 'Autor Desconocido'
                  }}</span>
                  <span class="publish-date">
                    {{
                      new Date(
                        featuredPost.published_at || featuredPost.created_at
                      ).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    }}
                  </span>
                </div>
              </div>
            </div>

            <div class="post-categories">
              <div class="category-tags">
                <span
                  v-for="postTag in featuredPost.post_tags"
                  :key="postTag.name"
                  class="category-tag"
                >
                  {{ postTag.name }}
                </span>
              </div>
              <span class="read-time">{{ getReadingTime(featuredPost) }} min lectura</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Guías Completas Section -->
    <section class="guides-section" v-if="!isLoading">
      <div class="container">
        <div class="section-header">
          <h2>Guías Completas</h2>
          <p class="section-subtitle">
            Explora nuestras guías detalladas sobre software CMMS y GMAO
          </p>
        </div>

        <div class="guides-grid">
          <router-link to="/cmms-colombia" class="guide-card-link">
            <article class="guide-card">
              <div class="guide-badge">
                <span>Guía Completa</span>
              </div>
              <div class="guide-content">
                <h3 class="guide-title">Software CMMS en Colombia: Guía Completa 2025</h3>
                <p class="guide-description">
                  Descubre cómo elegir el mejor software CMMS para tu empresa en Colombia.
                  Comparativa, precios, beneficios y todo lo que necesitas saber sobre sistemas de
                  gestión de mantenimiento en 2025.
                </p>
                <div class="guide-footer">
                  <span class="guide-type">Guía de Compra</span>
                  <span class="guide-arrow">→</span>
                </div>
              </div>
            </article>
          </router-link>

          <router-link to="/gmao-latinoamerica" class="guide-card-link">
            <article class="guide-card">
              <div class="guide-badge">
                <span>Guía Completa</span>
              </div>
              <div class="guide-content">
                <h3 class="guide-title">
                  ¿Qué es un GMAO y por qué es tan valioso para las empresas?
                </h3>
                <p class="guide-description">
                  Un GMAO (Gestión de Mantenimiento Asistido por Ordenador) es una plataforma
                  digital que centraliza toda la información relacionada con el mantenimiento de los
                  activos. Descubre cómo transforma la gestión operativa.
                </p>
                <div class="guide-footer">
                  <span class="guide-type">Guía Informativa</span>
                  <span class="guide-arrow">→</span>
                </div>
              </div>
            </article>
          </router-link>
        </div>
      </div>
    </section>

    <!-- Blog Posts Grid -->
    <section class="blog-posts" v-if="regularPosts.length > 0 && !isLoading">
      <div class="container">
        <div class="section-header">
          <h2>Todos los Artículos</h2>
          <button class="view-all-btn">Ver todos los artículos</button>
        </div>

        <div class="posts-grid">
          <router-link
            v-for="post in regularPosts"
            :key="post.id"
            :to="`/blog/${post.slug}`"
            class="post-card-link"
          >
            <article class="post-card">
              <div class="post-image">
                <img
                  :src="post.cover_image_url || '/placeholder-image.jpg'"
                  :alt="post.title"
                  loading="lazy"
                  decoding="async"
                />
              </div>

              <div class="post-content">
                <!--  <div class="post-meta">
                  <span class="post-category">{{
                    post.post_tags[0]?.name || 'Sin categoría'
                  }}</span>
                  <span class="post-date">{{
                    new Date(post.published_at || post.created_at).toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  }}</span>
                </div> -->

                <h3 class="post-title">{{ post.title }}</h3>
                <p class="post-excerpt">{{ getPostExcerpt(post) }}</p>

                <div class="post-footer">
                  <div class="post-author-info">
                    <div class="post-author-avatar">
                      <span>{{ (post.author_name || 'A').charAt(0) }}</span>
                    </div>
                    <span class="post-author">{{ post.author_name || 'Autor Desconocido' }}</span>
                  </div>
                  <div class="post-meta-footer">
                    <span class="post-read-time">{{ getReadingTime(post) }} min</span>
                    <span class="post-date-short">{{
                      new Date(post.published_at || post.created_at).toLocaleDateString('es-ES', {
                        month: 'short',
                        day: 'numeric',
                      })
                    }}</span>
                  </div>
                </div>

                <div v-if="post.post_tags && post.post_tags.length > 0" class="post-tags-footer">
                  <span v-for="tag in post.post_tags.slice(0, 2)" :key="tag.name" class="post-tag">
                    {{ tag.name }}
                  </span>
                </div>
              </div>
            </article>
          </router-link>
        </div>
      </div>
    </section>

    <Footer />
  </main>
</template>

<style scoped>
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Hero Section */
.blog-hero {
  padding: 100px 0 100px;
  background-image: url('../assets/images/bannerblog3.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
}

.blog-hero::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

.blog-hero .container {
  position: relative;
  z-index: 2;
}

/* Breadcrumbs ya están en el componente */

.hero-content h1 {
  font-size: 3.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 20px;
  line-height: 1.1;
}

.hero-description {
  font-size: 1.25rem;
  color: #e2e8f0;
  max-width: 600px;
  line-height: 1.6;
}

/* Featured Post */
.featured-post {
  padding: 80px 0;
  background: white;
}

.featured-content {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 60px;
  align-items: start;
}

.featured-image-link {
  text-decoration: none;
  color: inherit;
  display: block;
}

.featured-image {
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.12);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.featured-image:hover {
  transform: translateY(-8px);
  box-shadow: 0 32px 64px rgba(0, 0, 0, 0.18);
}

.featured-image img {
  width: 100%;
  height: 480px;
  object-fit: cover;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.featured-image:hover img {
  transform: scale(1.05);
}

.featured-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.85));
  padding: 60px 40px 40px;
  color: white;
}

.featured-overlay h2 {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.featured-details {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.featured-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: linear-gradient(45deg, #1297ef, #4ab7ff);
  color: white;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  width: fit-content;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.featured-badge svg {
  width: 14px;
  height: 14px;
}

.featured-excerpt {
  font-size: 1.125rem;
  line-height: 1.7;
  color: #475569;
  margin: 0;
}

.post-meta {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.author-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.author-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(45deg, #1297ef, #4ab7ff);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 18px;
  flex-shrink: 0;
}

.author-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.author-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 15px;
}

.publish-date {
  font-size: 13px;
  color: #64748b;
}

.post-categories {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

.category-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.category-tag {
  padding: 6px 14px;
  background: #f1f5f9;
  color: #475569;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.category-tag:hover {
  background: #e2e8f0;
  transform: translateY(-1px);
}

.read-time {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

.read-time::before {
  content: '⏱';
  font-size: 14px;
}

.read-time {
  padding: 6px 12px;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

/* Guías Completas Section */
.guides-section {
  padding: 80px 0;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
}

.guides-section .section-header {
  text-align: center;
  margin-bottom: 50px;
}

.guides-section .section-header h2 {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 12px;
  background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.section-subtitle {
  font-size: 1.125rem;
  color: #64748b;
  margin: 0;
}

.guides-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 32px;
}

.guide-card-link {
  text-decoration: none;
  color: inherit;
  display: block;
}

.guide-card {
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid transparent;
  position: relative;
  overflow: hidden;
}

.guide-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #1297ef, #4ab7ff);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.4s ease;
}

.guide-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 40px rgba(18, 151, 239, 0.15);
  border-color: #e2e8f0;
}

.guide-card:hover::before {
  transform: scaleX(1);
}

.guide-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: linear-gradient(45deg, #1297ef, #4ab7ff);
  color: white;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 20px;
}

.guide-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.guide-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.3;
  transition: color 0.3s ease;
}

.guide-card:hover .guide-title {
  color: #1297ef;
}

.guide-description {
  font-size: 1rem;
  line-height: 1.7;
  color: #64748b;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.guide-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;
  margin-top: auto;
  border-top: 1px solid #f1f5f9;
}

.guide-type {
  padding: 6px 14px;
  background: #f1f5f9;
  color: #475569;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.guide-arrow {
  font-size: 20px;
  color: #1297ef;
  font-weight: 600;
  transition: transform 0.3s ease;
}

.guide-card:hover .guide-arrow {
  transform: translateX(4px);
}

/* Blog Posts Grid */
.blog-posts {
  padding: 100px 0;
  background: #f8fafc;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 60px;
}

.section-header h2 {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.view-all-btn {
  padding: 12px 24px;
  background: linear-gradient(45deg, #1297ef, #4ab7ff);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.view-all-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(18, 151, 239, 0.3);
}

.posts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 32px;
}

.post-card-link {
  text-decoration: none;
  color: inherit;
  display: block;
}

.post-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #f1f5f9;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.post-card:hover {
  transform: translateY(-12px);
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.12);
  border-color: #e2e8f0;
}

.post-image {
  height: 240px;
  overflow: hidden;
  position: relative;
}

.post-image::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.1) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.post-card:hover .post-image::after {
  opacity: 1;
}

.post-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.post-card:hover .post-image img {
  transform: scale(1.1);
}

.post-content {
  padding: 28px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.post-meta {
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 16px;
}

.post-category {
  padding: 6px 14px;
  background: linear-gradient(45deg, #1297ef, #4ab7ff);
  color: white;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 12px;
}

.post-date {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.post-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 12px;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color 0.3s ease;
}

.post-card:hover .post-title {
  color: #1297ef;
}

.post-excerpt {
  color: #64748b;
  line-height: 1.7;
  margin-bottom: 20px;
  font-size: 0.9375rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
}

.post-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
  margin-top: auto;
}

.post-author-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.post-author-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(45deg, #1297ef, #4ab7ff);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 13px;
  flex-shrink: 0;
}

.post-author {
  color: #475569;
  font-weight: 600;
  font-size: 13px;
}

.post-meta-footer {
  display: flex;
  align-items: center;
  gap: 12px;
}

.post-read-time {
  color: #64748b;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.post-read-time::before {
  content: '⏱';
  font-size: 13px;
}

.post-date-short {
  color: #94a3b8;
  font-size: 12px;
  font-weight: 500;
}

.post-tags-footer {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
}

.post-tag {
  padding: 4px 10px;
  background: #f8fafc;
  color: #64748b;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  border: 1px solid #e2e8f0;
}

/* Newsletter Section */
.newsletter {
  padding: 80px 0;
  background: linear-gradient(135deg, #1297ef 0%, #4ab7ff 100%);
  color: white;
}

.newsletter-content {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
}

.newsletter-content h2 {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 20px;
}
.parrafo {
  margin: 0px 0px 40px 0px;
}

.newsletter-content p {
  font-size: 1.125rem;
  margin-bottom: 40px;
  opacity: 0.9;
}

.newsletter-form {
  display: flex;
  gap: 12px;
  max-width: 400px;
  margin: 0 auto;
}

.newsletter-input {
  flex: 1;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  outline: none;
}

.newsletter-btn {
  padding: 12px 24px;
  background: white;
  color: #1297ef;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.newsletter-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(255, 255, 255, 0.3);
}

/* ===== SKELETON LOADER STYLES ===== */

.skeleton-container {
  padding: 40px 0;
}

/* Featured Post Skeleton */
.featured-post-skeleton {
  margin-bottom: 60px;
}

.featured-skeleton-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: start;
}

.featured-skeleton-image {
  width: 100%;
  height: 400px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 12px;
}

.featured-skeleton-details {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.skeleton-author {
  display: flex;
  align-items: center;
  gap: 12px;
}

.skeleton-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-author-text {
  width: 120px;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-date {
  width: 180px;
  height: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-title {
  width: 100%;
  height: 32px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-tags {
  display: flex;
  gap: 8px;
}

.skeleton-tag {
  width: 80px;
  height: 24px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 12px;
}

/* Posts Grid Skeleton */
.posts-skeleton {
  margin-top: 60px;
}

.section-header-skeleton {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
}

.skeleton-section-title {
  width: 200px;
  height: 32px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-button {
  width: 150px;
  height: 40px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

.posts-grid-skeleton {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
}

.post-card-skeleton {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.skeleton-post-image {
  width: 100%;
  height: 200px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-post-content {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-post-category {
  width: 80px;
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 10px;
}

.skeleton-post-title {
  width: 100%;
  height: 24px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-post-excerpt {
  width: 100%;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-post-excerpt:nth-child(2) {
  width: 80%;
}

.skeleton-post-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}

.skeleton-post-author {
  width: 100px;
  height: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-post-date {
  width: 80px;
  height: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

/* Shimmer Animation */
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-content h1 {
    font-size: 2.5rem;
  }

  .featured-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .featured-image img {
    height: 360px;
  }

  .featured-overlay {
    padding: 40px 30px 30px;
  }

  .featured-overlay h2 {
    font-size: 1.5rem;
  }

  .posts-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .section-header {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }

  .section-header h2 {
    font-size: 2rem;
  }

  .posts-grid {
    grid-template-columns: 1fr;
  }

  .guides-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .guide-card {
    padding: 30px;
  }

  .guide-title {
    font-size: 1.25rem;
  }

  .newsletter-form {
    flex-direction: column;
  }

  .newsletter-content h2 {
    font-size: 2rem;
  }

  /* Skeleton Responsive */
  .featured-skeleton-content {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .featured-skeleton-image {
    height: 300px;
  }

  .posts-grid-skeleton {
    grid-template-columns: 1fr;
  }

  .section-header-skeleton {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 0 15px;
  }

  .blog-hero {
    padding: 60px 0 40px;
  }

  .hero-content h1 {
    font-size: 2rem;
  }

  .featured-post {
    padding: 40px 0;
  }

  .blog-posts {
    padding: 60px 0;
  }

  .guides-section {
    padding: 60px 0;
  }

  .guide-card {
    padding: 24px;
  }

  .guide-title {
    font-size: 1.125rem;
  }

  .guides-section .section-header h2 {
    font-size: 2rem;
  }
}
</style>
